import { NAV_PATHS } from '@modules/common/constants';
import { useIsCollectionsEnabled } from '@modules/common/hooks';
import { SaveFeedback } from '@modules/feedback/components/FeedbackCard/SaveFeedback';
import { useIsFeedbackOnFeedbackV2Enabled } from '@modules/feedback/hooks';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { OpenInNewRounded } from '@mui/icons-material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { IconButton, Link, Stack, Tooltip } from '@mui/material';
import { SummaryMessageUnit } from 'generated/graphql';
import { matchPath, useLocation } from 'react-router-dom';

interface AudioKeywordOrReasonSummaryActionsProps {
  onSnippetShare: () => void;
  onFlagSummary: () => void;
  isSummaryFlagged: boolean;
  isBeingPlayed?: boolean;
  sourceSnippetURL: string | undefined;
  sourceName: string;
  feedbackID: string;
  summary: SummaryMessageUnit;
  labelID?: string;
  getSnippetUrl: () => string;
  isFlagLoading?: boolean;
  summaryIndex: number;
}

export const AudioKeywordOrReasonSummaryActions = ({
  onSnippetShare,
  onFlagSummary,
  isSummaryFlagged = false,
  isBeingPlayed,
  sourceSnippetURL,
  sourceName,
  feedbackID,
  summary,
  labelID,
  getSnippetUrl,
  isFlagLoading,
  summaryIndex,
}: AudioKeywordOrReasonSummaryActionsProps) => {
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();
  const location = useLocation();

  const isCollectionsEnabled = useIsCollectionsEnabled();

  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const isCollectionRoute = matchPath(
    `/${NAV_PATHS.SAVED_COLLECTION}`,
    location.pathname,
  );

  const snippetID = summary.text;

  const isSavedSnippet = useIsSnippetOrFeedbackSaved({
    feedbackID,
    snippetID,
    labelID,
    unitIndex: summaryIndex,
  });

  const shouldHighlightActions = isBeingPlayed;

  const getIconColor = () => {
    if (shouldHighlightActions) {
      return isFeedbackRedesignEnabled
        ? 'text.primary'
        : 'primary.contrastText';
    }

    if (isCollectionsEnabled) {
      return 'text.secondary';
    }

    if (isSavedSnippet) {
      return 'text.primary';
    }

    return 'text.secondary';
  };

  const iconColor = getIconColor();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <SaveFeedback
        iconColor={iconColor}
        feedbackID={feedbackID}
        snippetID={snippetID}
        labelID={labelID}
        getSnippetUrl={getSnippetUrl}
        summaryIndex={summaryIndex}
        shouldShowRemoveFromCollectionButton={!!isCollectionRoute}
      />
      {sourceSnippetURL && (
        <Tooltip
          arrow
          title={`Open snippet in ${sourceName || 'Source'}`}
          disableInteractive
        >
          <IconButton
            LinkComponent={Link}
            href={sourceSnippetURL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <OpenInNewRounded
              fontSize="small"
              sx={{
                color: iconColor,
              }}
            />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip arrow title="Share snippet" disableInteractive>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSnippetShare();
          }}
        >
          <LinkIcon
            fontSize="small"
            sx={{
              color: iconColor,
            }}
          />
        </IconButton>
      </Tooltip>
      {isFeedbackOnFeedbackV2Enabled && !isSummaryFlagged && (
        <Tooltip arrow title="Flag summary as incorrect" disableInteractive>
          <IconButton
            disabled={isFlagLoading}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onFlagSummary();
            }}
          >
            <FlagOutlinedIcon
              fontSize="small"
              sx={{
                color: iconColor,
              }}
            />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
